"use client";

import { FC } from "react";
import { useMessages } from "../context/i18n";
import { common as enCommon } from "../dictionaries/en/common";
import Typography from "./Typography";

// The brand logos are rendered via CSS `mask-image` so we can paint them
// in any solid color (here `slate-500`, one step darker than the
// previous `slate-400` / `fill-slate-400` look) while still loading the
// SVG file natively from disk — no JS, no XHR, no DOM injection, no
// first-paint flash.
//
// Each logo needs an explicit `width` because CSS masks do not auto-size
// from intrinsic SVG dimensions. The `width` is derived from each SVG's
// own viewBox aspect ratio, normalized to the rendered height (24px /
// `h-6`). If you add a logo here, set `aspect` to
// `viewBoxWidth / viewBoxHeight` from its `<svg viewBox=...>`.
const HEIGHT_PX = 24;

type CompanyLogo = {
  file: string;
  name: string;
  aspect: number;
};

const COMPANY_LOGOS: CompanyLogo[] = [
  { file: "adidas.svg", name: "adidas", aspect: 220 / 51 },
  { file: "bbc.svg", name: "BBC", aspect: 210 / 61 },
  { file: "berkeley.svg", name: "UC Berkeley", aspect: 214 / 67 },
  { file: "bolt.svg", name: "Bolt", aspect: 140 / 81 },
  { file: "buzzfeed.svg", name: "BuzzFeed", aspect: 263 / 46 },
  { file: "google.svg", name: "Google", aspect: 251 / 85 },
  { file: "greenpeace.svg", name: "Greenpeace", aspect: 337 / 52 },
  { file: "harvard.svg", name: "Harvard University", aspect: 203 / 54 },
  { file: "lemonde.svg", name: "Le Monde", aspect: 336 / 61 },
  { file: "mercado_libre.svg", name: "Mercado Libre", aspect: 163 / 68 },
  { file: "netflix.svg", name: "Netflix", aspect: 226 / 61 },
  { file: "redbull.svg", name: "Red Bull", aspect: 211 / 51 },
  { file: "sciencespo.svg", name: "Sciences Po", aspect: 300 / 47 },
  { file: "spotify.svg", name: "Spotify", aspect: 213 / 63 },
  { file: "stanford.svg", name: "Stanford University", aspect: 234 / 49 },
  { file: "tiktok.svg", name: "TikTok", aspect: 251 / 73 },
];

const CompanyLogosMarquee: FC<{ className?: string }> = ({ className }) => {
  const m = useMessages("common", enCommon);
  return (
    <div className={`max-w-full overflow-hidden w-full ${className ?? ""}`}>
      <Typography
        variant="body1"
        className="text-slate-500 text-center mb-4 mx-auto block"
      >
        {m.usedBy}
      </Typography>
      <div className="relative flex overflow-hidden group w-full [mask-image:_linear-gradient(to_right,_transparent_0,_black_128px,_black_calc(100%_-_128px),_transparent_100%)] [-webkit-mask-image:_linear-gradient(to_right,_transparent_0,_black_128px,_black_calc(100%_-_128px),_transparent_100%)]">
        {[0, 1].map((copy) => (
          <div
            key={copy}
            aria-hidden={copy === 1}
            className="flex animate-marquee shrink-0 items-center gap-12 pr-12 py-4"
          >
            {COMPANY_LOGOS.map((logo, index) => {
              const widthPx = Math.round(HEIGHT_PX * logo.aspect);
              const url = `url('/svg/logos/${logo.file}')`;
              return (
                <span
                  key={`logo-${copy}-${index}`}
                  role="img"
                  aria-label={copy === 0 ? logo.name : undefined}
                  aria-hidden={copy === 1 ? "true" : undefined}
                  className="shrink-0 inline-block bg-slate-500 dark:bg-slate-200"
                  style={{
                    height: HEIGHT_PX,
                    width: widthPx,
                    WebkitMaskImage: url,
                    maskImage: url,
                    WebkitMaskRepeat: "no-repeat",
                    maskRepeat: "no-repeat",
                    WebkitMaskPosition: "center",
                    maskPosition: "center",
                    WebkitMaskSize: "contain",
                    maskSize: "contain",
                  }}
                />
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CompanyLogosMarquee;
